import { useState, useEffect } from "react";
import { Users, TrendingDown, Activity, AlertOctagon, Sparkles, Mail, MessageCircle, Phone, UserX, XCircle } from "lucide-react";
import SectionCard from "@/components/SectionCard";
import KpiCard from "@/components/KpiCard";
import DonutChart from "@/components/charts/DonutChart";
import PipelineCard from "@/components/pipeline/PipelineCard";
import PipelineBar from "@/components/pipeline/PipelineBar";
import PanelFugas from "@/components/pipeline/PanelFugas";
import PanelDistribucion from "@/components/pipeline/PanelDistribucion";
import PanelEvolucion from "@/components/pipeline/PanelEvolucion";
import StageDrawer from "@/components/pipeline/StageDrawer";
import DashboardFooter from "@/components/pipeline/DashboardFooter";
import ContactoDetailDrawer from "@/components/ContactoDetailDrawer";
import { FUNNELS_BY_TYPE, globalData, DATOS_POR_PERIODO, getContactoAgregadoByPeriodo, CANCELACIONES_POR_PERIODO, NOSHOW_POR_PERIODO, HOSPITALARIO_POR_PERIODO, COBRANZAS_POR_PERIODO } from "@/mocks/data";
import { PIPELINE_STAGES, PIPELINE_TRANSICIONES, PIPELINE_EVOLUCION, getPuntoCritico, PIPELINES_BY_TYPE, TRANSICIONES_BY_TYPE } from "@/mocks/pipelineData";
import { JOURNEY_TYPES, formatNum } from "@/lib/constants";
import { useFilters } from "@/context/FiltersContext";
import CancelacionesDrawer from "@/components/CancelacionesDrawer";
import NoShowDrawer from "@/components/NoShowDrawer";
import HospitalarioDrawer from "@/components/HospitalarioDrawer";
import CobranzasDrawer from "@/components/CobranzasDrawer";

export default function Dashboard() {
  const [tipo, setTipo] = useState("ambulatorio");
  const [selectedStage, setSelectedStage] = useState(null);
  const [contactoDrawer, setContactoDrawer] = useState(null); // "email" | "whatsapp" | null
  const [cancelDrawer, setCancelDrawer] = useState(false);
  const [noshowDrawer, setNoshowDrawer] = useState(false);
  const [hospDrawer, setHospDrawer] = useState(null); // null | "indicacion" | "crm" | "pabellon"
  const [cobranzasDrawer, setCobranzasDrawer] = useState(null); // null | "facturacion" | "vigente" | "mora_temprana" | "mora_avanzada"
  const { filters, applyFilters } = useFilters();
  const current = FUNNELS_BY_TYPE[tipo];
  const isGlobal = tipo === "global";

  // Datos reales del período seleccionado
  const periodoData = DATOS_POR_PERIODO[filters.periodo] || DATOS_POR_PERIODO["Abril 2026"];

  // Contacto proactivo agregado por período
  const contactoAgg = getContactoAgregadoByPeriodo(filters.periodo);

  // Cancelaciones y No-Show reales por período (PRM)
  const cancelData = CANCELACIONES_POR_PERIODO[filters.periodo] || CANCELACIONES_POR_PERIODO["Abril 2026"];
  const noshowData = NOSHOW_POR_PERIODO[filters.periodo] || NOSHOW_POR_PERIODO["Abril 2026"];

  // Hospitalario — datos reales por período
  const hospData = HOSPITALARIO_POR_PERIODO[filters.periodo] || HOSPITALARIO_POR_PERIODO["Abril 2026"];

  // Cobranzas — datos reales por período (ciclo_ingresos, solo 2025)
  const cobranzasData = COBRANZAS_POR_PERIODO[filters.periodo] || COBRANZAS_POR_PERIODO["Abril 2026"];

  // Pipeline dinámico según tipo de viaje
  const currentPipeline = PIPELINES_BY_TYPE[tipo] || PIPELINE_STAGES;
  const currentTransiciones = TRANSICIONES_BY_TYPE[tipo] || PIPELINE_TRANSICIONES;

  // Determinar punto crítico del pipeline actual
  const puntoCritico = getPuntoCritico(currentPipeline);

  // Resetear selección al cambiar tipo de viaje (sin abrir drawer automáticamente)
  useEffect(() => {
    setSelectedStage(null);
  }, [tipo]);

  const stageSeleccionada = currentPipeline.find((s) => s.id === selectedStage);
  const handleCloseDrawer = () => setSelectedStage(null);

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Hero */}
      <div
        className="rounded-2xl overflow-hidden relative"
        style={{ background: "linear-gradient(135deg,#4c1d95 0%,#6d28d9 33%,#7c3aed 66%,#3b82f6 100%)" }}
      >
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 20%, rgba(255,255,255,0.3) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255,255,255,0.2) 0%, transparent 50%)",
          }}
        />
        <div className="relative p-6 lg:p-8 text-white">
          <div className="flex items-center gap-2 text-xs font-medium text-violet-100 mb-2">
            <Sparkles className="w-3.5 h-3.5" /> Inteligencia Operacional de Salud
          </div>
          <h1 className="text-3xl lg:text-4xl font-bold tracking-tight">Viaje del Paciente</h1>
          <p className="mt-2 text-sm text-violet-100 max-w-2xl">
            Flujo de {formatNum(globalData.kpis.total_pacientes_unicos)} pacientes a través de
            16 ámbitos — {globalData.periodo}
          </p>
          {globalData.nota_periodo && (
            <p className="mt-1 text-xs text-violet-200/70 max-w-2xl">{globalData.nota_periodo}</p>
          )}

          <div className="mt-5 flex flex-wrap gap-2" data-testid="journey-type-selector">
            {JOURNEY_TYPES.map((j) => (
              <button
                key={j.key}
                data-testid={`journey-${j.key}`}
                onClick={() => setTipo(j.key)}
                className={`px-4 h-9 rounded-xl text-xs font-semibold transition-all border ${
                  tipo === j.key
                    ? "bg-white text-slate-900 border-white shadow-md"
                    : "bg-white/10 text-white border-white/20 hover:bg-white/20"
                }`}
              >
                {j.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* KPIs globales */}
      {/* KPIs globales — datos reales del período seleccionado */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {tipo === "hospitalario" ? (
          <>
            <KpiCard
              testid="kpi-indicaciones-qx"
              label="Indicaciones quirúrgicas"
              value={formatNum(hospData.indicaciones.total)}
              icon={Activity}
              accent="emerald"
            />
            <KpiCard
              testid="kpi-cirugias-realizadas"
              label="Cirugías realizadas"
              value={formatNum(hospData.cirugias.total)}
              icon={Activity}
              accent="amber"
            />
            <KpiCard
              testid="kpi-conversion-ind-cx"
              label="Conversión Indicación→Cirugía"
              value={`${hospData.conversion_indicacion_cirugia}`}
              suffix="%"
              icon={TrendingDown}
              accent="blue"
            />
            <KpiCard
              testid="kpi-pacientes-crm"
              label="Pacientes en CRM"
              value={formatNum(hospData.crm.pacientes)}
              icon={Users}
              accent="violet"
            />
          </>
        ) : tipo === "cobranzas" ? (
          <>
            <KpiCard
              testid="kpi-facturacion-total"
              label="Facturación total"
              value={`${formatNum(cobranzasData.monto_total_mm)}`}
              suffix="MM CLP"
              icon={Activity}
              accent="violet"
            />
            <KpiCard
              testid="kpi-episodios-cobr"
              label="Episodios"
              value={formatNum(cobranzasData.episodios)}
              icon={Users}
              accent="blue"
            />
            <KpiCard
              testid="kpi-cartera-vigente"
              label="Cartera vigente"
              value={`${formatNum(cobranzasData.vigente_0_30_mm)}`}
              suffix="MM CLP"
              icon={Activity}
              accent="emerald"
            />
            <KpiCard
              testid="kpi-pct-vigente"
              label="% Vigente"
              value={`${cobranzasData.pct_vigente}`}
              suffix="%"
              icon={TrendingDown}
              accent="amber"
            />
          </>
        ) : (
          <>
            <KpiCard
              testid="kpi-pacientes-unicos"
              label="Pacientes en el período"
              value={formatNum(periodoData.dg.pacientes)}
              icon={Users}
              accent="violet"
            />
            <KpiCard
              testid="kpi-atenciones"
              label="Consultas realizadas"
              value={formatNum(periodoData.dg.episodios)}
              icon={Activity}
              accent="blue"
            />
            <KpiCard
              testid="kpi-tasa-fuga"
              label="Tasa de fuga global"
              value={`${globalData.kpis.tasa_fuga}`}
              suffix="%"
              icon={TrendingDown}
              accent="rose"
              delta={2.3}
              deltaLabel="pp vs Q1 2025"
              tooltip="Porcentaje de pacientes que no completan el ciclo de atención"
            />
            <KpiCard
              testid="kpi-mayor-fuga"
              label={globalData.kpis.punto_mayor_fuga}
              value={`${globalData.kpis.pct_mayor_fuga}`}
              suffix="% no realiza"
              icon={AlertOctagon}
              accent="amber"
            />
          </>
        )}
      </div>

      {isGlobal ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <SectionCard
            title="Distribución por tipo de viaje"
            subtitle="Participación sobre el total de pacientes"
            testid="donut-tipos"
          >
            <DonutChart
              labels={globalData.resumen_por_tipo.map((t) => t.tipo)}
              series={globalData.resumen_por_tipo.map((t) => t.pacientes)}
              colors={globalData.resumen_por_tipo.map((t) => t.color)}
              height={360}
            />
          </SectionCard>
          <SectionCard title="Resumen por tipo" subtitle="Volumen y participación" testid="resumen-tipos">
            <div className="space-y-3">
              {globalData.resumen_por_tipo.map((t) => (
                <div key={t.tipo} className="flex items-center gap-3" data-testid={`resumen-${t.tipo}`}>
                  <div
                    className="h-10 w-10 rounded-xl flex items-center justify-center text-white font-bold text-xs"
                    style={{ backgroundColor: t.color }}
                  >
                    {t.pct}%
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-slate-900">{t.tipo}</div>
                    <div className="h-2 rounded-full bg-slate-100 mt-1 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${Math.min(t.pct * 1.4, 100)}%`, backgroundColor: t.color }}
                      />
                    </div>
                  </div>
                  <div className="text-sm font-bold text-slate-900 tabular-nums">{formatNum(t.pacientes)}</div>
                </div>
              ))}
            </div>
          </SectionCard>
        </div>
      ) : (
        <>
          {/* FILA SUPERIOR — Pipeline de 6 macro-etapas */}
          <div className={`grid gap-3 ${currentPipeline.length <= 3 ? "grid-cols-1 md:grid-cols-3" : "grid-cols-2 md:grid-cols-3 lg:grid-cols-6"}`} data-testid="pipeline-row">
            {currentPipeline.map((stage) => (
              <PipelineCard
                key={stage.id}
                stage={{ ...stage, volumen: stage.volumen != null ? applyFilters(stage.volumen) : null }}
                selected={selectedStage === stage.id}
                isCritico={puntoCritico === stage.id}
                onClick={(id) => setSelectedStage(selectedStage === id ? null : id)}
              />
            ))}
          </div>
          <PipelineBar stages={currentPipeline} />

          {/* FILA INTERMEDIA — 3 paneles de análisis */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="intermediate-row">
            <PanelFugas transiciones={currentTransiciones} applyFilters={applyFilters} />
            <PanelDistribucion stages={currentPipeline} applyFilters={applyFilters} />
            <PanelEvolucion evolucion={PIPELINE_EVOLUCION} />
          </div>

          {/* Drawer — Drill-down contextual */}
          <StageDrawer stage={stageSeleccionada} onClose={handleCloseDrawer} applyFilters={applyFilters} />

          {/* Hospitalario — Detalle por etapa con datos reales */}
          {tipo === "hospitalario" && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="hospitalario-detail-section">
              <div className="cursor-pointer" onClick={() => setHospDrawer("indicacion")} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && setHospDrawer("indicacion")}>
                <SectionCard title="Indicación Quirúrgica" subtitle={`org_indicaciones · ${filters.periodo}`} className="hover:shadow-lg hover:-translate-y-0.5 transition-all h-full" actions={<span className="text-xs text-teal-600 font-semibold">Ver detalle →</span>}>
                  <KpiCard label="Indicaciones" value={formatNum(hospData.indicaciones.total)} suffix={`${formatNum(hospData.indicaciones.pacientes)} pacientes`} icon={Activity} accent="emerald" />
                </SectionCard>
              </div>
              <div className="cursor-pointer" onClick={() => setHospDrawer("crm")} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && setHospDrawer("crm")}>
                <SectionCard title="CRM Quirúrgico" subtitle={`Keiron · ${filters.periodo}`} className="hover:shadow-lg hover:-translate-y-0.5 transition-all h-full" actions={<span className="text-xs text-lime-600 font-semibold">Ver detalle →</span>}>
                  <KpiCard label="En CRM" value={formatNum(hospData.crm.total)} suffix={`${formatNum(hospData.crm.pacientes)} pacientes`} icon={Users} accent="blue" />
                </SectionCard>
              </div>
              <div className="cursor-pointer" onClick={() => setHospDrawer("pabellon")} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && setHospDrawer("pabellon")}>
                <SectionCard title="Pabellón" subtitle={`SAP · ${filters.periodo}`} className="hover:shadow-lg hover:-translate-y-0.5 transition-all h-full" actions={<span className="text-xs text-amber-600 font-semibold">Ver detalle →</span>}>
                  <KpiCard label="Cirugías realizadas" value={formatNum(hospData.cirugias.total)} suffix={`${formatNum(hospData.cirugias.pacientes)} pacientes`} icon={Activity} accent="amber" />
                </SectionCard>
              </div>
            </div>
          )}

          {/* Cobranzas — Detalle por etapa con datos reales ciclo_ingresos */}
          {tipo === "cobranzas" && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="cobranzas-detail-section">
              <div className="cursor-pointer" onClick={() => setCobranzasDrawer("facturacion")} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && setCobranzasDrawer("facturacion")}>
                <SectionCard title="Facturación" subtitle={`${cobranzasData.mes_referencia}`} className="hover:shadow-lg hover:-translate-y-0.5 transition-all h-full" actions={<span className="text-xs text-violet-600 font-semibold">Ver detalle →</span>}>
                  <div className="text-2xl font-bold text-slate-900 tabular-nums">${formatNum(cobranzasData.monto_total_mm)} <span className="text-sm font-normal text-slate-500">MM</span></div>
                  <div className="text-xs text-slate-500 mt-1 tabular-nums">{formatNum(cobranzasData.episodios)} episodios</div>
                </SectionCard>
              </div>
              <div className="cursor-pointer" onClick={() => setCobranzasDrawer("vigente")} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && setCobranzasDrawer("vigente")}>
                <SectionCard title="Cartera Vigente" subtitle="0-30 días" className="hover:shadow-lg hover:-translate-y-0.5 transition-all h-full" actions={<span className="text-xs text-emerald-600 font-semibold">Ver detalle →</span>}>
                  <div className="text-2xl font-bold text-emerald-600 tabular-nums">${formatNum(cobranzasData.vigente_0_30_mm)} <span className="text-sm font-normal text-slate-500">MM</span></div>
                  <div className="text-xs text-slate-500 mt-1 tabular-nums">{cobranzasData.pct_vigente}% del total</div>
                </SectionCard>
              </div>
              <div className="cursor-pointer" onClick={() => setCobranzasDrawer("mora_temprana")} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && setCobranzasDrawer("mora_temprana")}>
                <SectionCard title="Mora Temprana" subtitle="31-60 días" className="hover:shadow-lg hover:-translate-y-0.5 transition-all h-full" actions={<span className="text-xs text-amber-600 font-semibold">Ver detalle →</span>}>
                  <div className="text-2xl font-bold text-amber-600 tabular-nums">${formatNum(cobranzasData.mora_31_60_mm)} <span className="text-sm font-normal text-slate-500">MM</span></div>
                  <div className="text-xs text-slate-500 mt-1 tabular-nums">{cobranzasData.pct_mora_temprana}%</div>
                </SectionCard>
              </div>
              <div className="cursor-pointer" onClick={() => setCobranzasDrawer("mora_avanzada")} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && setCobranzasDrawer("mora_avanzada")}>
                <SectionCard title="Mora Avanzada" subtitle="61-360+ días" className="hover:shadow-lg hover:-translate-y-0.5 transition-all h-full" actions={<span className="text-xs text-red-600 font-semibold">Ver detalle →</span>}>
                  <div className="text-2xl font-bold text-red-600 tabular-nums">${formatNum(cobranzasData.mora_61_180_mm + cobranzasData.mora_181_360_mm)} <span className="text-sm font-normal text-slate-500">MM</span></div>
                  <div className="text-xs text-slate-500 mt-1">61-180d + 181-360d</div>
                </SectionCard>
              </div>
            </div>
          )}

          {/* Contacto Proactivo — solo ambulatorio */}
          {current.contacto && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="contacto-proactivo-section">
              <div
                className="cursor-pointer"
                onClick={() => setContactoDrawer("email")}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && setContactoDrawer("email")}
              >
                <SectionCard
                  title="Contacto Proactivo — Email"
                  subtitle={`Motor de comunicaciones · ${filters.periodo} · Clic para detalle`}
                  testid="contacto-email"
                  className="hover:shadow-lg hover:-translate-y-0.5 transition-all h-full"
                  actions={<span className="text-xs text-violet-600 font-semibold">Ver detalle →</span>}
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <KpiCard
                      testid="kpi-emails-enviados"
                      label="Emails enviados"
                      value={formatNum(contactoAgg.email.enviados)}
                      icon={Mail}
                      accent="violet"
                    />
                    <KpiCard
                      testid="kpi-email-destinatarios"
                      label="Destinatarios"
                      value={formatNum(contactoAgg.email.destinatarios_est)}
                      icon={Users}
                      accent="blue"
                    />
                    <KpiCard
                      testid="kpi-email-open"
                      label="Open rate"
                      value={`${contactoAgg.email.open_rate}`}
                      suffix="%"
                      icon={Mail}
                      accent="emerald"
                    />
                    <KpiCard
                      testid="kpi-email-click"
                      label="Click rate"
                      value={`${contactoAgg.email.click_rate}`}
                      suffix="%"
                      icon={Mail}
                      accent="amber"
                    />
                  </div>
                </SectionCard>
              </div>

              <div
                className="cursor-pointer"
                onClick={() => setContactoDrawer("whatsapp")}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && setContactoDrawer("whatsapp")}
              >
                <SectionCard
                  title="Contacto Proactivo — WhatsApp"
                  subtitle={`Motor de comunicaciones · ${filters.periodo} · Clic para detalle`}
                  testid="contacto-whatsapp"
                  className="hover:shadow-lg hover:-translate-y-0.5 transition-all h-full"
                  actions={<span className="text-xs text-emerald-600 font-semibold">Ver detalle →</span>}
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <KpiCard
                      testid="kpi-whatsapp-enviados"
                      label="Mensajes enviados"
                      value={formatNum(contactoAgg.wsp.enviados)}
                      icon={MessageCircle}
                      accent="emerald"
                    />
                    <KpiCard
                      testid="kpi-whatsapp-telefonos"
                      label="Teléfonos únicos"
                      value={formatNum(contactoAgg.wsp.telefonos_est)}
                      icon={Phone}
                      accent="blue"
                    />
                    <KpiCard
                      testid="kpi-whatsapp-read"
                      label="Read rate"
                      value={`${contactoAgg.wsp.read_rate}`}
                      suffix="%"
                      icon={MessageCircle}
                      accent="violet"
                    />
                  </div>
                </SectionCard>
              </div>
            </div>
          )}

          {/* Cancelaciones y No-Show — datos reales PRM por período */}
          {tipo === "ambulatorio" && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="cancelaciones-noshow-section">
              <div
                className="cursor-pointer"
                onClick={() => setCancelDrawer(true)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && setCancelDrawer(true)}
              >
                <SectionCard
                  title="Cancelaciones"
                  subtitle={`${formatNum(cancelData.canceladas)} citas canceladas (${cancelData.pct_canceladas}%) · ${filters.periodo}`}
                  testid="cancelaciones-section"
                  className="hover:shadow-lg hover:-translate-y-0.5 transition-all h-full"
                  actions={<span className="text-xs text-rose-600 font-semibold">Ver detalle →</span>}
                >
                  <div className="mb-4">
                    <KpiCard
                      testid="kpi-cancelaciones-total"
                      label="Total cancelaciones"
                      value={formatNum(cancelData.canceladas)}
                      suffix={`(${cancelData.pct_canceladas}%)`}
                      icon={XCircle}
                      accent="rose"
                    />
                  </div>
                  <DonutChart
                    labels={cancelData.por_canal.map((c) => c.canal)}
                    series={cancelData.por_canal.map((c) => c.citas)}
                    colors={cancelData.por_canal.map((c) => c.color)}
                    height={280}
                  />
                </SectionCard>
              </div>

              <div
                className="cursor-pointer"
                onClick={() => setNoshowDrawer(true)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && setNoshowDrawer(true)}
              >
                <SectionCard
                  title="No-Show"
                  subtitle={`Consultas presenciales · ${filters.periodo}`}
                  testid="noshow-section"
                  className="hover:shadow-lg hover:-translate-y-0.5 transition-all h-full"
                  actions={<span className="text-xs text-amber-600 font-semibold">Ver detalle →</span>}
                >
                  <div className="flex flex-col items-center justify-center h-full gap-4 py-6">
                    <KpiCard
                      testid="kpi-noshow-total"
                      label="Citas no-show"
                      value={formatNum(noshowData.no_show)}
                      suffix={`de ${formatNum(noshowData.vigentes)} vigentes`}
                      icon={UserX}
                      accent="rose"
                      delta={noshowData.pct_no_show}
                      deltaLabel="tasa no-show"
                    />
                    <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 w-full">
                      <div className="text-xs font-semibold text-amber-600 uppercase tracking-wider">
                        Alineado con Equipo de Acceso
                      </div>
                      <p className="text-sm text-slate-700 mt-2">
                        {formatNum(noshowData.no_show)} citas vigentes sin asistencia = <strong className="tabular-nums">{noshowData.pct_no_show}%</strong> de no-show.
                        Fuente: <code className="text-xs bg-slate-100 px-1 rounded">canales.dbo.reservas2</code>
                      </p>
                    </div>
                  </div>
                </SectionCard>
              </div>
            </div>
          )}
        </>
      )}

      {/* Footer */}
      <DashboardFooter />

      {/* Drawer de detalle de contacto */}
      {contactoDrawer && current.contacto && (
        <ContactoDetailDrawer
          tipo={contactoDrawer}
          data={current.contacto}
          periodo={filters.periodo}
          onClose={() => setContactoDrawer(null)}
        />
      )}

      {/* Drawer de detalle de cancelaciones */}
      {cancelDrawer && (
        <CancelacionesDrawer
          data={cancelData}
          periodo={filters.periodo}
          onClose={() => setCancelDrawer(false)}
        />
      )}

      {/* Drawer de detalle de no-show */}
      {noshowDrawer && (
        <NoShowDrawer
          data={noshowData}
          periodo={filters.periodo}
          onClose={() => setNoshowDrawer(false)}
        />
      )}

      {/* Drawer de detalle hospitalario */}
      {hospDrawer && (
        <HospitalarioDrawer
          etapa={hospDrawer}
          data={hospData}
          periodo={filters.periodo}
          onClose={() => setHospDrawer(null)}
        />
      )}

      {/* Drawer de detalle cobranzas */}
      {cobranzasDrawer && (
        <CobranzasDrawer
          etapa={cobranzasDrawer}
          data={cobranzasData}
          periodo={filters.periodo}
          onClose={() => setCobranzasDrawer(null)}
        />
      )}
    </div>
  );
}
